import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const KEY = "sarthi_cookie_consent";  // "all" | "essential"

// Expose a small helper that other modules (AdSense wrapper, analytics, etc.) can query.
export function getCookieConsent() {
  try { return localStorage.getItem(KEY); } catch { return null; }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only if user has not made a choice yet.
    try {
      if (!localStorage.getItem(KEY)) setVisible(true);
    } catch { /* localStorage blocked — banner stays hidden */ }
  }, []);

  const setChoice = (value) => {
    try { localStorage.setItem(KEY, value); } catch { /* ignore */ }
    setVisible(false);
    // Broadcast to the app so analytics / ad wrappers can react without a reload.
    window.dispatchEvent(new CustomEvent("sarthi:cookie-consent", { detail: value }));
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[70] px-3 pb-3 print:hidden sm:px-6 sm:pb-6"
    >
      <div className="mx-auto max-w-4xl rounded-2xl border-2 border-sarthi-border bg-white p-4 shadow-2xl sm:p-5">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-sarthi-accent text-sarthi-primary">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-lg font-extrabold text-sarthi-text">We use cookies</h2>
            <p className="mt-1 text-sm font-medium text-sarthi-muted">
              We use essential cookies to run this site and, with your consent, analytics + advertising cookies (Google Analytics, Google AdSense, Razorpay) to improve your experience and support the platform. Read our{" "}
              <Link to="/cookie-policy" className="font-bold text-sarthi-primary underline">Cookie Policy</Link> or{" "}
              <Link to="/privacy" className="font-bold text-sarthi-primary underline">Privacy Policy</Link>.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                data-testid="cookie-accept-all"
                onClick={() => setChoice("all")}
                className="sarthi-btn-primary px-4 py-2 text-sm text-white"
              >
                Accept all
              </button>
              <button
                data-testid="cookie-essential-only"
                onClick={() => setChoice("essential")}
                className="sarthi-btn-outline px-4 py-2 text-sm font-bold"
              >
                Essential only
              </button>
            </div>
          </div>
          <button
            data-testid="cookie-dismiss"
            onClick={() => setChoice("essential")}
            aria-label="Dismiss"
            className="flex-none rounded-lg p-1 text-sarthi-muted hover:bg-sarthi-accent hover:text-sarthi-primary"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
